class SlideshowOverlay {
  constructor(container) {
    this.container = container;
    this.dismissButton = container.querySelector('[data-slideshow-overlay-dismiss]');
    this.slideshow = container.closest('.slideshow');
    this.slideshowComponent = container.closest('slideshow-component');
    this.storageKey = `slideshow-overlay:${container.dataset.storageKey || 'default'}`;
  }

  init() {
    if (this.container.dataset.overlayInitialized === 'true') return;
    this.container.dataset.overlayInitialized = 'true';

    if (this.hasDismissed()) {
      this.hide();
      this.resumeAutoplay();
      return;
    }

    this.show();
    this.pauseAutoplay();

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', this.handleDismiss.bind(this));
    }
  }

  hasDismissed() {
    try {
      return window.localStorage.getItem(this.storageKey) === 'dismissed';
    } catch (error) {
      return false;
    }
  }

  show() {
    this.slideshow?.classList.add('slideshow--overlay-active');
    this.container.removeAttribute('hidden');
  }

  hide() {
    this.slideshow?.classList.remove('slideshow--overlay-active');
    this.container.setAttribute('hidden', '');
  }

  pauseAutoplay() {
    if (!this.slideshowComponent) return;

    if (this.slideshowComponent.sliderAutoplayButton && this.slideshowComponent.autoplayButtonIsSetToPlay) {
      this.slideshowComponent.autoPlayToggle();
      this.shouldResumeAutoplay = true;
      return;
    }

    if (typeof this.slideshowComponent.pause === 'function') {
      this.slideshowComponent.pause();
    }
  }

  resumeAutoplay() {
    if (!this.slideshowComponent) return;

    if (this.shouldResumeAutoplay && this.slideshowComponent.sliderAutoplayButton && !this.slideshowComponent.autoplayButtonIsSetToPlay) {
      this.slideshowComponent.autoPlayToggle();
      this.shouldResumeAutoplay = false;
      return;
    }

    if (
      this.slideshowComponent.slider?.getAttribute('data-autoplay') === 'true' &&
      typeof this.slideshowComponent.play === 'function' &&
      !this.slideshowComponent.sliderAutoplayButton
    ) {
      this.slideshowComponent.play();
    }
  }

  handleDismiss() {
    try {
      window.localStorage.setItem(this.storageKey, 'dismissed');
    } catch (error) {
      // Ignore storage errors and still close the overlay for this page view.
    }

    this.hide();
    this.resumeAutoplay();
  }
}

class SlideshowSlideOverlay {
  constructor(container) {
    this.container = container;
    this.dismissButton = container.querySelector('[data-slideshow-overlay-dismiss]') || container;
    this.slide = container.closest('.slideshow__slide');
    this.slideshowComponent = container.closest('slideshow-component');
    this.storageKey = `slideshow-overlay:${container.dataset.storageKey || 'default'}`;
  }

  init() {
    if (this.container.dataset.overlayInitialized === 'true') return;
    this.container.dataset.overlayInitialized = 'true';

    if (this.hasDismissed()) {
      this.hide();
      return;
    }

    this.show();
    this.pauseAutoplay();
    this.updateTabindex();
    this.slideshowComponent?.addEventListener('slideChanged', this.updateTabindex.bind(this));

    if (this.dismissButton) {
      this.dismissButton.addEventListener('click', this.handleDismiss.bind(this));
    }
  }

  hasDismissed() {
    try {
      return window.localStorage.getItem(this.storageKey) === 'dismissed';
    } catch (error) {
      return false;
    }
  }

  show() {
    this.slide?.classList.add('slideshow__slide--overlay-active');
    this.container.removeAttribute('hidden');
    this.updateTabindex();
  }

  hide() {
    this.slide?.classList.remove('slideshow__slide--overlay-active');
    this.container.setAttribute('hidden', '');
    this.container.setAttribute('tabindex', '-1');
  }

  updateTabindex() {
    if (this.container.hasAttribute('hidden')) return;

    if (this.slide?.getAttribute('aria-hidden') === 'true') {
      this.container.setAttribute('tabindex', '-1');
      return;
    }

    this.container.removeAttribute('tabindex');
  }

  pauseAutoplay() {
    if (!this.slideshowComponent) return;

    if (this.slideshowComponent.sliderAutoplayButton && this.slideshowComponent.autoplayButtonIsSetToPlay) {
      this.slideshowComponent.autoPlayToggle();
      this.shouldResumeAutoplay = true;
      return;
    }

    if (typeof this.slideshowComponent.pause === 'function') {
      this.slideshowComponent.pause();
    }
  }

  resumeAutoplay() {
    if (!this.slideshowComponent || this.hasActiveSlideOverlays()) return;

    if (this.shouldResumeAutoplay && this.slideshowComponent.sliderAutoplayButton && !this.slideshowComponent.autoplayButtonIsSetToPlay) {
      this.slideshowComponent.autoPlayToggle();
      this.shouldResumeAutoplay = false;
      return;
    }

    if (
      this.slideshowComponent.slider?.getAttribute('data-autoplay') === 'true' &&
      typeof this.slideshowComponent.play === 'function' &&
      !this.slideshowComponent.sliderAutoplayButton
    ) {
      this.slideshowComponent.play();
    }
  }

  hasActiveSlideOverlays() {
    return Boolean(this.slideshowComponent?.querySelector('[data-slideshow-slide-overlay]:not([hidden])'));
  }

  hideMatchingOverlays() {
    if (!this.slideshowComponent) {
      this.hide();
      return;
    }

    this.slideshowComponent.querySelectorAll('[data-slideshow-slide-overlay]').forEach((overlay) => {
      const storageKey = `slideshow-overlay:${overlay.dataset.storageKey || 'default'}`;
      if (storageKey !== this.storageKey) return;

      overlay.closest('.slideshow__slide')?.classList.remove('slideshow__slide--overlay-active');
      overlay.setAttribute('hidden', '');
      overlay.setAttribute('tabindex', '-1');
    });
  }

  handleDismiss() {
    try {
      window.localStorage.setItem(this.storageKey, 'dismissed');
    } catch (error) {
      // Ignore storage errors and still close the overlay for this page view.
    }

    this.hideMatchingOverlays();
    this.resumeAutoplay();
    this.slideshowComponent?.dispatchEvent(new CustomEvent('slideshowOverlayDismissed'));
  }
}

class SlideshowVideoController {
  constructor(slideshowComponent) {
    this.slideshowComponent = slideshowComponent;
    this.videos = Array.from(slideshowComponent.querySelectorAll('[data-slideshow-video]'));
    this.isInViewport = true;
    this.handleSlideChange = this.update.bind(this);
  }

  init() {
    if (!this.videos.length || this.slideshowComponent.dataset.videoControllerInitialized === 'true') return;

    this.slideshowComponent.dataset.videoControllerInitialized = 'true';
    this.videos.forEach((video) => {
      video.muted = true;
      video.pause();
    });

    this.slideshowComponent.addEventListener('slideChanged', this.handleSlideChange);
    this.slideshowComponent.addEventListener('slideshowOverlayDismissed', this.handleSlideChange);
    document.addEventListener('visibilitychange', this.handleSlideChange);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.isInViewport = entries.some((entry) => entry.isIntersecting);
          this.update();
        },
        { threshold: 0.15 }
      );
      this.observer.observe(this.slideshowComponent);
    }

    this.update();
  }

  canPlay(video) {
    if (document.hidden || !this.isInViewport) return false;
    if (video.dataset.requiresMatureDismiss !== 'true') return true;

    try {
      return window.localStorage.getItem(`slideshow-overlay:${video.dataset.storageKey || 'default'}`) === 'dismissed';
    } catch (error) {
      return false;
    }
  }

  update() {
    const activeSlide =
      this.slideshowComponent.sliderItemsToShow?.[this.slideshowComponent.currentPage - 1] ||
      this.slideshowComponent.querySelector('.slideshow__slide[aria-hidden="false"]') ||
      this.slideshowComponent.querySelector('.slideshow__slide');

    this.videos.forEach((video) => {
      const shouldPlay = video.closest('.slideshow__slide') === activeSlide && this.canPlay(video);

      if (!shouldPlay) {
        video.pause();
        return;
      }

      video.muted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }
}

function initSlideshowOverlays(root = document) {
  root.querySelectorAll('[data-slideshow-overlay]').forEach((overlay) => {
    const slideshowOverlay = new SlideshowOverlay(overlay);
    slideshowOverlay.init();
  });

  root.querySelectorAll('[data-slideshow-slide-overlay]').forEach((overlay) => {
    const slideshowSlideOverlay = new SlideshowSlideOverlay(overlay);
    slideshowSlideOverlay.init();
  });

  root.querySelectorAll('slideshow-component').forEach((slideshowComponent) => {
    const slideshowVideoController = new SlideshowVideoController(slideshowComponent);
    slideshowVideoController.init();
  });
}

document.addEventListener('DOMContentLoaded', () => initSlideshowOverlays());
document.addEventListener('shopify:section:load', (event) => initSlideshowOverlays(event.target));
